import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { responseMsg } from "../../common/ResponseMsg.js";

import { useAlert } from '../../../shared/components/AlertProvider';
import Flex from '../../../shared/components/Flex';
import ModalButton from '../../../shared/components/ModalButton';
import PrettySelect from '../../../shared/components/PrettySelect';
import Spacer from '../../../shared/components/Spacer';
import useDevice from '../../../shared/hooks/useDevice';

function SmsModal({ closeSmsModal, userId }) {
    const { handleSubmit } = useForm();
    const device = useDevice();
    const isMobile = device !== 'desktop';
    const [smsCodes, setSmsCodes] = useState({ all: [], my: [] });
    const [checkBox, setCheckBox] = useState({ userId, codes: [] });
    const myIdSet = useMemo(() => {
        return new Set(smsCodes.my.map(item => item.id));
    }, [smsCodes.my]);
    const showAlert = useAlert();

    const isAllChecked = useMemo(() => {
        return (
            smsCodes.all.length > 0 &&
            smsCodes.all.length === smsCodes.my.length
        );
    }, [smsCodes.all.length, smsCodes.my.length]);

    const insertIcon = (list) => {
        return list.map(item => {
            let icon = null;

            switch (item.id) {
                case 43: icon = '/images/img_temp.png'; break;
                case 44: icon = '/images/img_snow.png'; break;
                case 45: icon = '/images/img_warn.png'; break;
                case 46: icon = '/images/img_wind.png'; break;
                case 47: icon = '/images/img_sun.png'; break;
                case 295: icon = '/images/img_snow_detect.png'; break;
                default: break;
            }

            return { ...item, icon };
        });
    };

    useEffect(() => {
        fetch(`/api/system/codes/SENSOR/42/${userId}`)
            .then(res => res.json())
            .then(json => {
                if (json.code === responseMsg.SUCCESS.code) {
                    setSmsCodes({
                        all: insertIcon(json.data.all),
                        my: json.data.my
                    });

                    setCheckBox({
                        userId,
                        codes: json.data.my.map(ele => ({ id: ele.id }))
                    });
                } else {
                    showAlert({title: "경고", text: "통신이 지연되고 있습니다.", type: "warning"});
                }
            })
            .catch(console.error);
    }, [userId]);

    const handleItemToggle = (id) => {
        setSmsCodes(prev => {
            const exists = prev.my.some(item => item.id === id);

            const nextMy = exists
                ? prev.my.filter(item => item.id !== id)
                : [...prev.my, { id }];

            return { ...prev, my: nextMy };
        });

        setCheckBox(prev => {
            const exists = prev.codes.some(code => code.id === id);

            return {
                ...prev,
                codes: exists
                    ? prev.codes.filter(code => code.id !== id)
                    : [...prev.codes, { id }]
            };
        });
    };

    const handleSelectAll = () => {
        const nextMy = isAllChecked
            ? []
            : smsCodes.all.map(item => ({ id: item.id }));

        setSmsCodes(prev => ({ ...prev, my: nextMy }));
        setCheckBox(prev => ({ ...prev, codes: nextMy }));
    };

    const onSubmitHandler = () => {
        fetch(`/api/system/users/sms`, {
            method: "PATCH",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(checkBox),
        })
            .then(res => res.json())
            .then(json => {
                if (json.code === responseMsg.SUCCESS.code) {
                    showAlert({title: "알림", text: "수정이 완료됐습니다."});
                    closeSmsModal();
                } else {
                    showAlert({title: "경고", text: "통신이 지연되고 있습니다.", type: "warning"});
                }
            })
            .catch(console.error);
    };

    return (
        <>
            <div>
                <PrettySelect
                    text="전체"
                    name="sms"
                    value="전체"
                    isSelectAll
                    fullWidth
                    variant="dashed"
                    style={{ height: "58px" }}
                    rightIcon={<img src="/images/ico_check_orange.png" alt="" />}
                    checked={isAllChecked}
                    onClick={handleSelectAll}
                />

                <Spacer size={16} direction="vertical" />

                {/* 개별 */}
                {smsCodes.all.map((item, idx) => (
                    <div key={item.id}>
                        <PrettySelect
                            name="sms"
                            text={item.name}
                            value={item.name}
                            variant="dashed"
                            style={{ height: "58px" }}
                            leftIcon={<img src={item.icon} alt=" " style={{width: 22}} />}
                            rightIcon={<img src="/images/ico_check_orange.png" alt="" />}
                            fullWidth
                            checked={myIdSet.has(item.id)}
                            onClick={() => handleItemToggle(item.id)}
                        />
                        {idx !== smsCodes.all.length - 1 && (
                            <Spacer size={16} direction="vertical" />
                        )}
                    </div>
                ))}
            </div>

            <Flex
                flexDirection={isMobile ? "column" : "row"}
                fullWidth
                justifyContent="end"
                gap={10}
            >
                <ModalButton type="update" onClick={handleSubmit(onSubmitHandler)} />
                <ModalButton type="cancel" onClick={closeSmsModal} />
            </Flex>
        </>
    );
}

export default SmsModal;
